import { useEffect, useState } from "react";
import { Container, Row, Col, Card, Spinner, Alert, Button, Form, Table } from "react-bootstrap";
import { getDashboardData } from "../../services/api";
// import Header from "../../components/header/header";
// import Cover from "../../components/cover/cover";
import "./AnalyticsDashboard.css";
import { fetchFacilitiesForFilters, fetchSportsForFilters, selectFacilities, selectSports } from "../../redux/slices/pitchFiltersSlice";
import { fetchPitches } from "../../redux/viewPitchSlice";
import { useDispatch, useSelector } from "react-redux";

// Skeleton components for loading states
const SkeletonCard = () => (
  <Card className="kpi-card">
    <Card.Body className="text-center">
      <div className="skeleton skeleton-kpi-value mb-2"></div>
      <div className="skeleton skeleton-kpi-label"></div>
    </Card.Body>
  </Card>
);

const SkeletonChart = ({ title }) => (
  <Card>
    <Card.Header>
      <h5>{title}</h5>
    </Card.Header>
    <Card.Body>
      <div className="skeleton-chart">
        <div className="skeleton skeleton-chart-item mb-3"></div>
        <div className="skeleton skeleton-chart-item mb-3"></div>
        <div className="skeleton skeleton-chart-item mb-3"></div>
        <div className="skeleton skeleton-chart-item"></div>
      </div>
    </Card.Body>
  </Card>
);

const SkeletonTable = ({ title }) => (
  <Card>
    <Card.Header>
      <h5>{title}</h5>
    </Card.Header>
    <Card.Body>
      <div className="skeleton-table">
        <div className="skeleton skeleton-table-header mb-3"></div>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="skeleton skeleton-table-row mb-2"></div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

// No Data Skeleton components that show structure but with "No Data" text
const NoDataCard = () => (
  <Card className="kpi-card">
    <Card.Body className="text-center">
      <div className="no-data-kpi-value mb-2">--</div>
      <div className="no-data-kpi-label">No Data</div>
    </Card.Body>
  </Card>
);

const NoDataChart = ({ title }) => (
  <Card>
    <Card.Header>
      <h5>{title}</h5>
    </Card.Header>
    <Card.Body>
      <div className="no-data-chart">
        <div className="no-data-chart-item mb-3">
          <div className="no-data-label">No Data Available</div>
          <div className="no-data-bar"></div>
          <div className="no-data-value">--</div>
        </div>
        <div className="no-data-chart-item mb-3">
          <div className="no-data-label">No Data Available</div>
          <div className="no-data-bar"></div>
          <div className="no-data-value">--</div>
        </div>
        <div className="no-data-chart-item mb-3">
          <div className="no-data-label">No Data Available</div>
          <div className="no-data-bar"></div>
          <div className="no-data-value">--</div>
        </div>
        <div className="no-data-chart-item">
          <div className="no-data-label">No Data Available</div>
          <div className="no-data-bar"></div>
          <div className="no-data-value">--</div>
        </div>
      </div>
    </Card.Body>
  </Card>
);

const NoDataTable = ({ title }) => (
  <Card>
    <Card.Header>
      <h5>{title}</h5>
    </Card.Header>
    <Card.Body>
      <div className="no-data-table">
        <div className="no-data-table-header mb-3">
          <div className="no-data-table-cell">Facility/Sport</div>
          <div className="no-data-table-cell">Bookings</div>
          <div className="no-data-table-cell">Revenue</div>
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="no-data-table-row mb-2">
            <div className="no-data-table-cell">No Data Available</div>
            <div className="no-data-table-cell">--</div>
            <div className="no-data-table-cell">--</div>
          </div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

const AnalyticsDashboard = () => {

  const dispatch = useDispatch();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get facilities and sports from pitchFiltersSlice
  const facilitiesFromFilters = useSelector(selectFacilities);
  const sports = useSelector(selectSports);


  const [selectedFacility, setSelectedFacility] = useState('');
  const [filteredSports, setFilteredSports] = useState([]);
  const [filteredPitches, setFilteredPitches] = useState([]);
  const [availablePitches, setAvailablePitches] = useState([]);
  const [hasApplied, setHasApplied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    period: '6months',
    facility: '',
    sport: '',
    pitch: '',
    from_date: '',
    to_date: ''
  });

  // Period options
  const periodOptions = [
    { value: '1year', label: '1 Year' },
    { value: 'custom', label: 'Custom' }
  ];

  const toMMDDYYYY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${m}-${d}-${y}`;
  };

  const clean = (obj) => {
    const base = {
      period: obj.period,
      facility: obj.facility || '',
      sport: obj.sport || '',
      pitch: obj.pitch || ''
    };
    if (obj.period === 'custom' && obj.from_date && obj.to_date) {
      base.startDate = toMMDDYYYY(obj.from_date);
      base.endDate = toMMDDYYYY(obj.to_date);
    }
    return base;
  };

  // Fetch dashboard data
  const fetchDashboardData = async (filterParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDashboardData(filterParams);
      console.log('Dashboard data:', response?.data?.data);
      setDashboardData(response?.data?.data);
    } catch (err) {
      console.log('Failed to fetch dashboard data');
      setError(err?.message || 'Failed to fetch dashboard data');
      setLoading(false);
    } finally {

      setLoading(false);
    }
  };

  // Fetch facilities, sports, and pitches on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pitchesRes] = await Promise.all([
          dispatch(fetchFacilitiesForFilters()),
          dispatch(fetchSportsForFilters()),
          dispatch(fetchPitches())
        ]);

        if (pitchesRes?.payload) {
          setAvailablePitches(pitchesRes.payload);
          console.log('Available Pitches Set:', pitchesRes.payload);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setError('Failed to load facilities, sports, and pitches. Please refresh the page.');
        setLoading(false)
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    fetchDashboardData();
    // fetchFilterOptions();
    setHasApplied(true); // treat initial load as applied so subsequent changes auto-apply
  }, []);

  // Get filtered sports based on selected facility
  const getFilteredSports = () => {
    const currentFacility = filters.facility || selectedFacility;
    if (!currentFacility || !facilitiesFromFilters || !sports) return [];
    const selectedFacilityData = facilitiesFromFilters.find(f => f._id === currentFacility);
    if (!selectedFacilityData || !selectedFacilityData.availableSports) return [];

    return sports.filter(sport =>
      selectedFacilityData.availableSports.some(availableSport =>
        availableSport._id === sport._id
      )
    );
  };

  // Get filtered pitches based on selected facility and sport
  const getFilteredPitches = () => {
    const currentFacility = filters.facility || selectedFacility;
    const currentSport = filters.sport;

    if (!currentFacility || !currentSport || !facilitiesFromFilters || !sports || !availablePitches) {
      console.log('Missing required data:', { currentFacility, currentSport, facilitiesFromFilters, sports, availablePitches });
      return [];
    }

    // Find the selected facility and sport names
    const selectedFacilityData = facilitiesFromFilters.find(f => f._id === currentFacility);
    const selectedSportData = sports.find(s => s._id === currentSport);

    if (!selectedFacilityData?.name || !selectedSportData?.name) {
      console.log('Missing facility or sport data:', { selectedFacilityData, selectedSportData });
      return [];
    }

    // Filter pitches based on facility name and sport name
    return availablePitches.filter(pitch => {
      if (!pitch?.facilityName || !pitch?.sportName) {
        console.log('Invalid pitch data:', pitch);
        return false;
      }

      const facilityNameMatch = String(pitch.facilityName).toLowerCase() === String(selectedFacilityData.name).toLowerCase();
      const sportNameMatch = String(pitch.sportName) === String(selectedSportData.name);

      console.log(`Checking Pitch ${pitch.name}:`, {
        facilityMatch: facilityNameMatch,
        sportMatch: sportNameMatch
      });

      return facilityNameMatch && sportNameMatch;
    });
  };

  // Update sports when facility changes
  useEffect(() => {
    const currentFacility = filters.facility || selectedFacility;
    if (currentFacility) {
      const filtered = getFilteredSports();
      setFilteredSports(filtered);
    } else {
      setFilteredSports([]);
    }
  }, [filters.facility, selectedFacility, facilitiesFromFilters, sports]);

  // Update pitches when facility or sport changes
  useEffect(() => {
    const currentFacility = filters.facility || selectedFacility;
    if (currentFacility && filters.sport) {
      const filtered = getFilteredPitches();
      setFilteredPitches(filtered);
    } else {
      setFilteredPitches([]);
    }
  }, [filters.facility, filters.sport, selectedFacility, availablePitches, facilitiesFromFilters, sports]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (key === 'facility') {
      newFilters.sport = '';
      newFilters.pitch = '';
      setSelectedFacility(value);
    } else if (key === 'sport') {
      newFilters.pitch = '';
    }
    setFilters(newFilters);

    // Auto-apply only when NOT in custom mode
    if (hasApplied) {
      const newIsCustom = newFilters.period === 'custom';
      const prevIsCustom = filters.period === 'custom';
      if (!newIsCustom && !prevIsCustom) {
        fetchDashboardData(clean(newFilters));
      }
    }
  };

  const handleApplyFilters = () => {
    // If custom period is selected, require both dates
    if (filters.period === 'custom' && (!filters.from_date || !filters.to_date)) {
      return; // silently block; UI disables button too
    }
    fetchDashboardData(clean(filters));
    setHasApplied(true);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchDashboardData(clean(filters));
    } finally {
      setRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedFacility('');
    setFilteredSports([]);
    setFilteredPitches([]);
    const defaultFilters = { period: '6months', facility: '', sport: '', pitch: '', from_date: '', to_date: '' };
    setFilters(defaultFilters);
    // Immediately fetch defaults to reflect clear on screen
    fetchDashboardData(clean(defaultFilters));
    setHasApplied(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show error as a dismissible alert at the top if there's an error
  const ErrorAlert = () => {
    if (error == null) return null;

    return (
      <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
        <Alert.Heading>Error Loading Dashboard</Alert.Heading>
        <p>{error}</p>
        <hr />
        <p className="mb-0">
          Please check your network connection and try again. If the problem persists, contact support.
        </p>
      </Alert>
    );
  };

  if (loading && !dashboardData) {
    return (
      <div>
        {/* <Header /> */}
        {/* <Cover breadcrumb={breadcrumb} page="dashboard" /> */}
        <Container fluid className="analytics-page">
          {/* Error Alert */}
          <ErrorAlert />

          {/* Filters Section */}
          <Card className="filter-card mb-4">
            <Card.Body>
              <div className="filter-loading-overlay">
                <div className="filter-loading-message">
                  <Spinner size="sm" className="me-2" />
                  Loading filters...
                </div>
              </div>
              <Row className="g-3 align-items-end">
                <Col md={3}>
                  <Form.Label>Time Period</Form.Label>
                  <Form.Select disabled>
                    <option>Loading...</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Facility</Form.Label>
                  <Form.Select disabled>
                    <option>Loading...</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Sport</Form.Label>
                  <Form.Select disabled>
                    <option>Loading...</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label>Pitch/Court</Form.Label>
                  <Form.Select disabled>
                    <option>Loading...</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Skeleton KPI Cards */}
          <Row className="mb-4">
            <Col md={3}><SkeletonCard /></Col>
            <Col md={3}><SkeletonCard /></Col>
            <Col md={3}><SkeletonCard /></Col>
            <Col md={3}><SkeletonCard /></Col>
          </Row>

          {/* Skeleton Charts */}
          <Row className="mb-4">
            <Col lg={6}><SkeletonChart title="Revenue by Date" /></Col>
            <Col lg={6}><SkeletonChart title="Bookings by Date" /></Col>
          </Row>

          {/* Skeleton Revenue Charts */}
          <Row className="mb-4">
            <Col lg={6}><SkeletonChart title="Revenue by Sport" /></Col>
            <Col lg={6}><SkeletonChart title="Revenue by Pitch" /></Col>
          </Row>

          {/* Skeleton Tables */}
          <Row className="mb-4">
            <Col lg={6}><SkeletonTable title="Facility Performance" /></Col>
            <Col lg={6}><SkeletonTable title="Sport Performance" /></Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div>
        {/* <Header /> */}
        {/* <Cover breadcrumb={breadcrumb} page="dashboard" /> */}
        <Container fluid className="analytics-page">
          {/* Error Alert */}
          <ErrorAlert />

          {/* Filters Section - Fully Functional */}
          <Card className="filter-card mb-4">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={3}>
                  <Form.Label>Time Period</Form.Label>
                  <Form.Select
                    value={filters.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                  >
                    {periodOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {filters.period === 'custom' && (
                  <>
                    <Col md={3}>
                      <Form.Label>From</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.from_date || ''}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>To</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.to_date || ''}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                      />
                    </Col>
                  </>
                )}

                <Col md={3}>
                  <Form.Label>Facility</Form.Label>
                  <Form.Select
                    value={filters.facility}
                    onChange={(e) => handleFilterChange('facility', e.target.value)}
                  >
                    <option value="">All Facilities</option>
                    {facilitiesFromFilters.map(facility => (
                      <option key={facility._id} value={facility._id}>
                        {facility.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Label>Sport</Form.Label>
                  <Form.Select
                    value={filters.sport}
                    onChange={(e) => handleFilterChange('sport', e.target.value)}
                    disabled={!filters.facility}
                  >
                    <option value="">All Sports</option>
                    {filteredSports.map(sport => (
                      <option key={sport._id} value={sport._id}>
                        {sport.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Label>Pitch/Court</Form.Label>
                  <Form.Select
                    value={filters.pitch}
                    onChange={(e) => handleFilterChange('pitch', e.target.value)}
                    disabled={!filters.facility || !filters.sport}
                  >
                    <option value="">All Pitches</option>
                    {filteredPitches.length === 0 && filters.facility && filters.sport ? (
                      <option value="" disabled>No pitches available (API issue)</option>
                    ) : (
                      filteredPitches.map(pitch => (
                        <option key={pitch._id} value={pitch._id}>
                          {pitch.name}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      onClick={handleApplyFilters}
                      disabled={loading || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                      className="flex-grow-1 btn-apply-analytics"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Loading...
                        </>
                      ) : (
                        'Apply Filters'
                      )}
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={handleRefresh}
                      disabled={loading || refreshing || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                      title="Refresh"
                      style={{ whiteSpace: 'nowrap', minWidth: 90 }}
                    >
                      {refreshing ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearFilters}
                      disabled={loading}
                      className="btn-clear"
                    >
                      Clear
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* No Data KPI Cards */}
          <Row className="mb-4">
            <Col md={3}><NoDataCard /></Col>
            <Col md={3}><NoDataCard /></Col>
            <Col md={3}><NoDataCard /></Col>
            <Col md={3}><NoDataCard /></Col>
          </Row>

          {/* No Data Charts */}
          <Row className="mb-4">
            <Col lg={6}><NoDataChart title="Revenue by Date" /></Col>
            <Col lg={6}><NoDataChart title="Bookings by Date" /></Col>
          </Row>

          {/* No Data Revenue Charts */}
          <Row className="mb-4">
            <Col lg={6}><NoDataChart title="Revenue by Sport" /></Col>
            <Col lg={6}><NoDataChart title="Revenue by Pitch" /></Col>
          </Row>

          {/* No Data Tables */}
          <Row className="mb-4">
            <Col lg={6}><NoDataTable title="Facility Performance" /></Col>
            <Col lg={6}><NoDataTable title="Sport Performance" /></Col>
          </Row>
        </Container>
      </div>
    );
  }

  const { kpis, charts, tables } = dashboardData;

  return (
    <>
      <div>
        {/* <Header /> */}
        {/* <Cover breadcrumb={breadcrumb} page="dashboard" /> */}

        <Container fluid className="analytics-page">

          {/* Error Alert */}
          <ErrorAlert />

          {/* Filters Section */}
          <Card className="filter-card mb-4">
            <Card.Body>
              {loading && (
                <div className="filter-loading-overlay">
                  <div className="filter-loading-message">
                    <Spinner size="sm" className="me-2" />
                    Applying filters...
                  </div>
                </div>
              )}
              <Row className="g-3 align-items-end">
                <Col md={3}>
                  <Form.Label>Time Period</Form.Label>
                  <Form.Select
                    value={filters.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                  >
                    {periodOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {filters.period === 'custom' && (
                  <>
                    <Col md={3}>
                      <Form.Label>From</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.from_date || ''}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>To</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.to_date || ''}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                      />
                    </Col>
                  </>
                )}

                <Col md={3}>
                  <Form.Label>Facility</Form.Label>
                  <Form.Select
                    value={filters.facility}
                    onChange={(e) => handleFilterChange('facility', e.target.value)}
                  >
                    <option value="">All Facilities</option>
                    {facilitiesFromFilters.map(facility => (
                      <option key={facility._id} value={facility._id}>
                        {facility.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Label>Sport</Form.Label>
                  <Form.Select
                    value={filters.sport}
                    onChange={(e) => handleFilterChange('sport', e.target.value)}
                    disabled={!filters.facility}
                  >
                    <option value="">All Sports</option>
                    {filteredSports.map(sport => (
                      <option key={sport._id} value={sport._id}>
                        {sport.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <Form.Label>Pitch/Court</Form.Label>
                  <Form.Select
                    value={filters.pitch}
                    onChange={(e) => handleFilterChange('pitch', e.target.value)}
                    disabled={!filters.facility || !filters.sport}
                  >
                    <option value="">All Pitches</option>
                    {filteredPitches.length === 0 && filters.facility && filters.sport ? (
                      <option value="" disabled>No pitches available (API issue)</option>
                    ) : (
                      filteredPitches.map(pitch => (
                        <option key={pitch._id} value={pitch._id}>
                          {pitch.name}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Col>

                <Col md={3}>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      onClick={handleApplyFilters}
                      disabled={loading || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                      className="flex-grow-1 btn-apply-analytics"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Loading...
                        </>
                      ) : (
                        'Apply Filters'
                      )}
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={handleRefresh}
                      disabled={loading || refreshing || (filters.period === 'custom' && (!filters.from_date || !filters.to_date))}
                      title="Refresh"
                      style={{ whiteSpace: 'nowrap', minWidth: 90 }}
                    >
                      {refreshing ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearFilters}
                      disabled={loading}
                      className="btn-clear"
                    >
                      Clear
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* KPI Cards */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="kpi-card">
                <Card.Body className="text-center">
                  <h3 className="kpi-value text-primary">{kpis.totalBookedSlots}</h3>
                  <p className="kpi-label">Total Booked Slots</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="kpi-card">
                <Card.Body className="text-center">
                  <h3 className="kpi-value text-success">{kpis.availableSlots}</h3>
                  <p className="kpi-label">Available Slots</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="kpi-card">
                <Card.Body className="text-center">
                  <h3 className="kpi-value text-warning">{formatCurrency(kpis.totalRevenue)}</h3>
                  <p className="kpi-label">Total Revenue</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="kpi-card">
                <Card.Body className="text-center">
                  <h3 className="kpi-value text-info">{formatCurrency(kpis.avgRevenuePerBooking)}</h3>
                  <p className="kpi-label">Avg Revenue/Booking</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts Section */}
          <Row className="mb-4">
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Revenue by Date</h5>
                </Card.Header>
                <Card.Body>
                  {Array.isArray(charts.revenueByDate) && charts.revenueByDate.length > 0 ? (
                    <div className="chart-container">
                      {charts.revenueByDate.map((item, index) => (
                        <div key={index} className="chart-item">
                          <div className="chart-label">{formatDate(item.date)}</div>
                          <div className="chart-bar">
                            <div
                              className="chart-fill"
                              style={{
                                width: `${(item.revenue / Math.max(...charts.revenueByDate.map(d => d.revenue))) * 100}%`
                              }}
                            ></div>
                          </div>
                          <div className="chart-value">{formatCurrency(item.revenue)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No revenue data available</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Bookings by Date</h5>
                </Card.Header>
                <Card.Body>
                  {Array.isArray(charts.bookingsByDate) && charts.bookingsByDate.length > 0 ? (
                    <div className="chart-container">
                      {charts.bookingsByDate.map((item, index) => (
                        <div key={index} className="chart-item">
                          <div className="chart-label">{formatDate(item.date)}</div>
                          <div className="chart-bar">
                            <div
                              className="chart-fill bookings"
                              style={{
                                width: `${(item.bookings / Math.max(...charts.bookingsByDate.map(d => d.bookings))) * 100}%`
                              }}
                            ></div>
                          </div>
                          <div className="chart-value">{item.bookings} bookings</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No booking data available</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Revenue by Sport and Pitch */}
          <Row className="mb-4">
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Revenue by Sport</h5>
                </Card.Header>
                <Card.Body>
                  {Array.isArray(charts.revenueBySport) && charts.revenueBySport.length > 0 ? (
                    <div className="chart-container">
                      {charts.revenueBySport.filter(item => item.revenue > 0).map((item, index) => (
                        <div key={index} className="chart-item">
                          <div className="chart-label">{item.sportName || 'Unknown'}</div>
                          <div className="chart-bar">
                            <div
                              className="chart-fill sport"
                              style={{
                                width: `${(item.revenue / Math.max(...charts.revenueBySport.map(d => d.revenue))) * 100}%`
                              }}
                            ></div>
                          </div>
                          <div className="chart-value">{formatCurrency(item.revenue)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No sport revenue data available</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Revenue by Pitch</h5>
                </Card.Header>
                <Card.Body>
                  {Array.isArray(charts.revenueByPitch) && charts.revenueByPitch.length > 0 ? (
                    <div className="chart-container">
                      {charts.revenueByPitch.filter(item => item.revenue > 0).map((item, index) => (
                        <div key={index} className="chart-item">
                          <div className="chart-label">{item.pitchName || 'Unknown'}</div>
                          <div className="chart-bar">
                            <div
                              className="chart-fill pitch"
                              style={{
                                width: `${(item.revenue / Math.max(...charts.revenueByPitch.map(d => d.revenue))) * 100}%`
                              }}
                            ></div>
                          </div>
                          <div className="chart-value">{formatCurrency(item.revenue)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No pitch revenue data available</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data Tables */}
          <Row className="mb-4">
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Facility Performance</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Facility</th>
                        <th>Booked SLots</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tables.facilityData) && tables.facilityData.map((facility, index) => (
                        <tr key={index}>
                          <td>{facility.facilityName}</td>
                          <td>{facility.totalBooking}</td>
                          <td>{formatCurrency(facility.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Header>
                  <h5>Sport Performance</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Sport</th>
                        <th>Booked SLots</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tables.sportsData) && tables.sportsData.filter(sport => sport.totalBooking > 0).map((sport, index) => (
                        <tr key={index}>
                          <td>{sport.sportName || 'Unknown'}</td>
                          <td>{sport.totalBooking}</td>
                          <td>{formatCurrency(sport.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Booking Details Table */}
          {/* <Card className="mb-4">
            <Card.Header>
              <h5>Recent Booking Details</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Booked By</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Facility</th>
                    <th>Sport</th>
                    <th>Pitch</th>
                    <th>Slots</th>
                    <th>Revenue</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(tables.bookingDetails) && tables.bookingDetails.map((booking, index) => (
                    <tr key={index}>
                      <td>{booking.bookingId}</td>
                      <td>{booking.bookedBy}</td>
                      <td>{formatDate(booking.date)}</td>
                      <td>{booking.timing}</td>
                      <td>{booking.facilityName}</td>
                      <td>{booking.sportName}</td>
                      <td>{booking.pitchName}</td>
                      <td>{booking.numberOfSlots}</td>
                      <td>{formatCurrency(booking.revenue)}</td>
                      <td>
                        <span className={`badge ${booking.paymentType === 'cash' ? 'bg-success' : 'bg-primary'}`}>
                          {booking.paymentType}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card> */}
        </Container>
      </div>
    </>
  );
};

export default AnalyticsDashboard;
